// Sessions.tsx — the full-screen Sessions page.
//
// A single dual-pane card that fills the viewport:
//   left  = the user's workspaces, each heading the sessions that run on
//           it as color-coded outline buttons,
//   right = markdown-rendered chat for the selected session, with a text
//           input at the bottom that streams the assistant reply
//           token-by-token.
//
// A workspace is the machine — a sessionenv Pod and its working tree —
// and a session is a conversation on it. Several sessions can share one
// workspace's files, packages and editor; suspending, resuming and Code
// OSS act on the workspace. The metrics + GPU graphs live on the separate
// Metrics page.
//
// The page is socket-driven. It holds one connection to the manager — the
// session list, and each row's live pod phase — and one to every ready
// session's harness, for what is happening inside that conversation. The
// split follows ownership: the manager owns the rows and the Pods, so it
// is the only thing that can say a session is still provisioning (there
// is nothing running yet to ask), while a harness owns its own turns.
// See live/sessionLive.ts.
//
// What that replaces is a page that only learned anything by asking. The
// list refreshed on a pair of timers fired after each turn, so a
// regenerated title landed seconds late; a session created in another tab
// never appeared at all; a reply streamed only into the tab that had sent
// the message; and every other conversation in the sidebar looked idle
// whatever it was actually doing.
//
// This file is the composition root: it owns the state several parts
// share (the lists, the selection, the transcript, the composer's text)
// and wires the hooks and panes together. Each concern lives in its own
// module —
//   hooks       useSessionSync (sockets + REST load), useSessionPhase,
//               useSessionHistory, useTranscript, useTurnRouting,
//               useWorkspaceLifecycle, useSessionActions, useModelPicker,
//               useSessionCost, useMcpPrefs, usePaneWidth;
//   panes       SessionSidebar / SessionRow, LifecycleBanner,
//               TranscriptPane / MessageTurn, Composer / ModelPicker,
//               McpPrefsModal;
//   pure logic  chatState, transcript, sessionRows, sessionFormat,
//               modelPicker, turnState, workspaceGroups (all unit-tested).
//
// Hook order matters where two effects write the same state; see
// useSessionPhase.ts. The socket effects run in the order the page has
// always run them: manager, harnesses, tool-UI sink, focus.
import React, { useState, useEffect, useRef, useMemo, useSyncExternalStore } from 'react';
import { ui } from 'common';
import CodeOssPane from './CodeOssPane';
import Composer from './Composer';
import LifecycleBanner from './LifecycleBanner';
import McpPrefsModal from './McpPrefsModal';
import SessionSidebar from './SessionSidebar';
import TranscriptPane from './TranscriptPane';
import { byRecency } from './chatState';
import {
  activityOf,
  getSnapshot as getLiveSnapshot,
  subscribe as subscribeLive,
} from './live/sessionLive';
import { withoutKey } from './sessionRows';
import { focusedSessionId } from './turnState';
import { useMcpPrefs } from './useMcpPrefs';
import { useModelPicker } from './useModelPicker';
import { usePaneWidth } from './usePaneWidth';
import { useSessionActions } from './useSessionActions';
import { useSessionCost } from './useSessionCost';
import { useSessionHistory } from './useSessionHistory';
import { useSessionPhase } from './useSessionPhase';
import { useSessionSync } from './useSessionSync';
import { useTranscript } from './useTranscript';
import { useTurnRouting } from './useTurnRouting';
import { useWorkspaceLifecycle } from './useWorkspaceLifecycle';
import { groupByWorkspace, workspaceIdOf } from './workspaceGroups';
import type { Session, SessionMessage, Workspace } from '../api';

export const Sessions: React.FC = () => {
  // Sessions list + selection, and the workspaces they run in.
  const [sessions, setSessions] = useState<Session[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // In-progress edits to session titles, keyed by session id. A row
  // renders its draft when one exists and the stored name otherwise —
  // without that split, the turn-end regen landing while someone is
  // halfway through typing would overwrite the field mid-keystroke.
  const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({});

  // Local chat state for the selected session. Kept separate from
  // `sessions` so token-by-token deltas don't churn the left pane.
  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [input, setInput] = useState('');
  // editorEpoch is bumped to force a clean Milkdown instance — used after
  // send (clear) and to reseed it with a message given back.
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [streamError, setStreamError] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Chat or the workspace's Code OSS. codeWorkspaceId is the workspace
  // whose editor has been opened: it stays mounted (hidden) while the
  // user is back on a chat, stays open across that workspace's sessions,
  // and goes when they switch to a session in another workspace.
  const [view, setView] = useState<'chat' | 'code'>('chat');
  const [codeWorkspaceId, setCodeWorkspaceId] = useState<string | null>(null);

  // Destructive actions confirm through the shared Modal, not window.confirm
  // (DESIGN.md C-MSG-3). `confirmDialog` renders at the end of the tree.
  const [confirm, confirmDialog] = ui.useConfirm();

  // The session the right pane is showing, and its workspace: whose Pod
  // the banner describes and whose editor the Code button opens.
  const selected = sessions.find((s) => s.id === selectedId) ?? null;
  const selectedWorkspaceId = selected ? workspaceIdOf(selected) : null;
  const codeOpen = view === 'code' && codeWorkspaceId !== null && codeWorkspaceId === selectedWorkspaceId;

  // Everything the sockets know: each row's pod phase, and each session's
  // turn status. Read through useSyncExternalStore because the sockets
  // deliberately outlive this component — see live/sessionLive.ts.
  const live = useSyncExternalStore(subscribeLive, getLiveSnapshot, getLiveSnapshot);
  const selectedActivity = activityOf(live, selectedId ?? '');

  const { sessionPhase, setSessionPhase, bootLogs, setBootLogs } = useSessionPhase(
    selectedId,
    selectedId ? live.phases[selectedId] : undefined,
  );
  const picker = useModelPicker({ selected, setSessions, setStreamError });
  const pane = usePaneWidth();
  const { historyLoadedId, loadingSession } = useSessionHistory({
    selectedId, setMessages, setStreamError,
  });
  const focusedId = focusedSessionId(historyLoadedId, selectedId);
  const refreshSessions = useSessionSync({
    sessions, phases: live.phases, focusedId,
    setSessions, setWorkspaces, setSelectedId, setCodeWorkspaceId,
  });
  const writers = useTranscript(setMessages);
  const lifecycle = useWorkspaceLifecycle({
    selectedWorkspaceId, refreshSessions, setSessionPhase, setBootLogs, setStreamError,
  });
  const turns = useTurnRouting({
    selectedId, sessions, live, selectedActivity, focusedId, sessionPhase,
    input, setInput, setEditorEpoch, setMessages, setStreamError, writers,
    handleResume: lifecycle.handleResume,
  });
  const sessionCost = useSessionCost(selectedId, turns.streaming);
  const mcp = useMcpPrefs();
  const actions = useSessionActions({
    selected, selectedId, newModel: picker.newModel, askConfirm: confirm, refreshSessions,
    setSelectedId, setSessions, setWorkspaces, setTitleDrafts, setStreamError,
  });

  // Auto-scroll to the bottom whenever messages grow (incl. streaming).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, turns.streaming, bootLogs]);

  // The sidebar: workspaces, each with its sessions, most recently talked
  // in first at both levels. Rows arrive over the socket one at a time and
  // are applied in place, so without re-sorting here a session that just
  // took a turn would keep the slot it had when the page loaded. See
  // workspaceGroups.ts for the rules.
  const groups = useMemo(
    () => groupByWorkspace(workspaces, [...sessions].sort(byRecency), (id): Workspace => ({
      id, name: 'Workspace', owner_email: '', created_at: '', updated_at: '',
    })),
    [workspaces, sessions],
  );

  return (
    <>
      {/* No card chrome — the dual pane fills the page directly. The
          height subtracts the sticky navbar + footer so the panes scroll
          internally rather than the whole page. */}
      <div className="d-flex" style={{ height: 'calc(100vh - 140px)', minHeight: 480 }}>
        <SessionSidebar
          width={pane.width}
          groups={groups}
          selectedId={selectedId}
          live={live}
          titleDrafts={titleDrafts}
          creating={actions.creating}
          onToggleSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
          onDraftChange={(id, value) => setTitleDrafts((prev) => ({ ...prev, [id]: value }))}
          onDraftClear={(id) => setTitleDrafts((prev) => withoutKey(prev, id))}
          onNewSession={actions.handleNewSession}
          onRenameSession={actions.handleRenameSession}
          onAutoTitle={actions.handleAutoTitle}
          onDeleteSession={actions.handleDeleteSession}
          onRenameWorkspace={actions.handleRenameWorkspace}
          onDeleteWorkspace={actions.handleDeleteWorkspace}
        />

        {/* Draggable divider. The 1px visible border sits inside a 6px
            wide hit zone so the cursor finds it easily without making
            the line itself look thick. */}
        <div
          onMouseDown={pane.onDividerDown}
          role="separator"
          aria-orientation="vertical"
          title="Drag to resize"
          style={{
            width: 6,
            cursor: 'col-resize',
            flexShrink: 0,
            background: 'transparent',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
          }}
        />

        {/* Right pane: chat */}
        <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
          {!selected ? (
            <div className="d-flex align-items-center justify-content-center text-muted flex-grow-1 text-center px-3">
              {sessions.length === 0
                ? 'Pick your tools and model below, then click New workspace to start a session.'
                : 'Select a session on the left, or pick a model below and start one with New workspace or a workspace\'s +.'}
            </div>
          ) : (
            <>
              <LifecycleBanner
                sessionPhase={sessionPhase}
                managerSocket={live.manager}
                sessionSocket={selectedActivity.socket}
                codeOpen={codeOpen}
                onToggleCode={() => {
                  if (codeOpen) {
                    setView('chat');
                  } else {
                    setCodeWorkspaceId(selectedWorkspaceId);
                    setView('code');
                  }
                }}
                workspaceId={selectedWorkspaceId}
                onCodeLaunchError={(msg) => setStreamError(`Could not open VS Code: ${msg}`)}
                forking={actions.forking}
                onFork={actions.handleFork}
                sessionCost={sessionCost}
                lifecycleBusy={lifecycle.lifecycleBusy}
                onSuspend={lifecycle.handleSuspend}
                onResume={lifecycle.handleResume}
              />
              {codeWorkspaceId !== null && codeWorkspaceId === selectedWorkspaceId && (
                <CodeOssPane key={codeWorkspaceId} workspaceId={codeWorkspaceId} visible={view === 'code'} />
              )}
              <TranscriptPane
                scrollRef={scrollRef}
                hidden={codeOpen}
                sessionPhase={sessionPhase}
                bootLogs={bootLogs}
                loadingSession={loadingSession}
                messages={messages}
                selectedId={selectedId}
                streaming={turns.streaming}
                streamingId={turns.streamingId}
                streamError={streamError}
                statusLine={selectedActivity.status === 'running' ? selectedActivity.statusLine : ''}
              />
            </>
          )}

          <Composer
            selectedId={selected ? selected.id : null}
            queued={turns.queued}
            onCancelQueued={turns.cancelQueued}
            input={input}
            onInputChange={setInput}
            onSend={turns.handleSend}
            editorEpoch={editorEpoch}
            streaming={turns.streaming}
            sessionPhase={sessionPhase}
            mcpEnabledCount={mcp.enabled.length}
            onOpenTools={mcp.openModal}
            picker={picker}
          />
        </div>
      </div>

      <McpPrefsModal prefs={mcp} />

      {confirmDialog}
    </>
  );
};

export default Sessions;
